import { Select } from '@quickreport/ui';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect, useState } from 'react';

export const PAGE_SIZES = [25, 50, 100, 200];
export const DEFAULT_PAGE = 50;

const fmt = new Intl.NumberFormat('en-IN');

/** Which rows are showing, and buttons to move through the rest. Hidden when there is nothing to page. */
export function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  noun,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
  onPageSize: (n: number) => void;
  noun: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const [jump, setJump] = useState(String(page));
  useEffect(() => setJump(String(page)), [page]);
  if (total === 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);
  const go = (p: number) => onPage(Math.min(Math.max(1, p), pages));
  const btn =
    'grid h-8 w-8 place-items-center rounded-lg border border-line bg-paper text-navy hover:bg-brand-soft disabled:pointer-events-none disabled:opacity-40';

  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-3 px-1 py-3 text-[13px]"
      aria-label={`${noun} pages`}
    >
      <div className="flex items-center gap-3 text-slate">
        <span aria-live="polite" className="whitespace-nowrap">
          Showing {fmt.format(first)}–{fmt.format(last)} of {fmt.format(total)} {noun}
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          Per page
          <Select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
            className="h-8 w-20"
            aria-label="Rows per page"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className={btn}
          aria-label="First page"
          disabled={page <= 1}
          onClick={() => go(1)}
        >
          <ChevronsLeft size={15} />
        </button>
        <button
          type="button"
          className={btn}
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => go(page - 1)}
        >
          <ChevronLeft size={15} />
        </button>
        <form
          className="flex items-center gap-1.5 px-1 text-slate"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(jump);
            if (Number.isFinite(n)) go(n);
          }}
        >
          Page
          <input
            value={jump}
            onChange={(e) => setJump(e.target.value.replace(/\D/g, ''))}
            onBlur={() => setJump(String(page))}
            inputMode="numeric"
            aria-label="Page number"
            className="h-8 w-14 rounded-lg border border-line bg-paper px-2 text-center text-ink outline-none focus:border-brand"
          />
          of {fmt.format(pages)}
        </form>
        <button
          type="button"
          className={btn}
          aria-label="Next page"
          disabled={page >= pages}
          onClick={() => go(page + 1)}
        >
          <ChevronRight size={15} />
        </button>
        <button
          type="button"
          className={btn}
          aria-label="Last page"
          disabled={page >= pages}
          onClick={() => go(pages)}
        >
          <ChevronsRight size={15} />
        </button>
      </div>
    </nav>
  );
}
